/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useRef, useState } from 'react'

export type MediaGenerateRequest = {
  prompt: string
  aspectRatio: string
  agentId: string
}

export type MediaAddToLibraryRequest = {
  url: string
  agentId: string
}

export type MediaEditRequest = {
  url: string
  agentId: string
  crop: {
    x: number
    y: number
    width: number
    height: number
  }
  adjust: {
    brightness: number
    contrast: number
    saturation: number
    grayscale: number
    blur: number
  }
}

export type MediaGeneratePanelProps = {
  prompt: string
  agentId?: string | null
  onPromptChange: (nextPrompt: string) => void
  onGenerate: (request: MediaGenerateRequest) => Promise<void> | void
  isGenerating?: boolean
  generatedUrls?: string[]
  selectedRatio?: string
  ratioOptions?: string[]
  onRatioChange?: (ratio: string) => void
  onAddToLibrary?: (request: MediaAddToLibraryRequest) => Promise<void> | void
  isAddingToLibrary?: boolean
  onApplyEdit?: (request: MediaEditRequest) => Promise<string | void> | string | void
  onOutputsChange?: (urls: string[], agentId: string) => void
  title?: string
  promptPlaceholder?: string
  generateLabel?: string
  generatingLabel?: string
  addToLibraryLabel?: string
  addingToLibraryLabel?: string
  resultLabel?: string
}

const DEFAULT_RATIO_OPTIONS = ['auto', '1:1', '4:5', '5:4', '16:9', '9:16', '3:2', '2:3']

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Unable to load image for editing.'))
    image.src = url
  })

const applyLocalEditToUrl = async (
  sourceUrl: string,
  crop: MediaEditRequest['crop'],
  adjust: MediaEditRequest['adjust']
): Promise<Blob> => {
  const image = await loadImage(sourceUrl)
  const width = image.naturalWidth || image.width
  const height = image.naturalHeight || image.height
  if (!width || !height) {
    throw new Error('Image dimensions are invalid.')
  }

  const sourceX = Math.round((clamp(crop.x, 0, 99) / 100) * width)
  const sourceY = Math.round((clamp(crop.y, 0, 99) / 100) * height)
  const sourceWidth = Math.max(1, Math.round((clamp(crop.width, 1, 100) / 100) * width))
  const sourceHeight = Math.max(1, Math.round((clamp(crop.height, 1, 100) / 100) * height))

  const safeWidth = Math.min(sourceWidth, Math.max(1, width - sourceX))
  const safeHeight = Math.min(sourceHeight, Math.max(1, height - sourceY))

  const canvas = document.createElement('canvas')
  canvas.width = safeWidth
  canvas.height = safeHeight
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Canvas context is unavailable.')
  }

  const brightness = clamp(adjust.brightness, 20, 220)
  const contrast = clamp(adjust.contrast, 20, 220)
  const saturation = clamp(adjust.saturation, 0, 240)
  const grayscale = clamp(adjust.grayscale, 0, 100)
  const blur = clamp(adjust.blur, 0, 12)

  context.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) grayscale(${grayscale}%) blur(${blur}px)`
  context.drawImage(image, sourceX, sourceY, safeWidth, safeHeight, 0, 0, safeWidth, safeHeight)

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result)
        return
      }
      reject(new Error('Could not produce edited image output.'))
    }, 'image/png')
  })

  return blob
}

export const MediaGeneratePanel = ({
  prompt,
  agentId,
  onPromptChange,
  onGenerate,
  isGenerating = false,
  generatedUrls = [],
  selectedRatio = 'auto',
  ratioOptions = DEFAULT_RATIO_OPTIONS,
  onRatioChange,
  onAddToLibrary,
  isAddingToLibrary = false,
  onApplyEdit,
  onOutputsChange,
  title = 'Generate Image',
  promptPlaceholder = 'Describe the image you want to create',
  generateLabel = 'Generate',
  generatingLabel = 'Generating...',
  addToLibraryLabel = 'Add to Library',
  addingToLibraryLabel = 'Adding...',
  resultLabel = 'Generated Results',
}: MediaGeneratePanelProps) => {
  const objectUrlsRef = useRef<string[]>([])
  const [workingUrls, setWorkingUrls] = useState<string[]>(generatedUrls)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [cropX, setCropX] = useState(0)
  const [cropY, setCropY] = useState(0)
  const [cropWidth, setCropWidth] = useState(100)
  const [cropHeight, setCropHeight] = useState(100)
  const [brightness, setBrightness] = useState(100)
  const [contrast, setContrast] = useState(100)
  const [saturation, setSaturation] = useState(100)
  const [grayscale, setGrayscale] = useState(0)
  const [blur, setBlur] = useState(0)
  const [isApplyingEdit, setIsApplyingEdit] = useState(false)
  const [editError, setEditError] = useState('')

  useEffect(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrlsRef.current = []
    setWorkingUrls(generatedUrls)
    setSelectedIndex(0)
    setEditError('')
  }, [generatedUrls])

  useEffect(() => {
    if (selectedIndex < workingUrls.length) return
    setSelectedIndex(workingUrls.length > 0 ? workingUrls.length - 1 : 0)
  }, [selectedIndex, workingUrls.length])

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      objectUrlsRef.current = []
    }
  }, [])

  const selectedUrl = useMemo(
    () => workingUrls[selectedIndex] || workingUrls[0] || '',
    [selectedIndex, workingUrls]
  )

  const canGenerate = Boolean(String(agentId || '').trim()) && Boolean(prompt.trim()) && !isGenerating
  const canAddToLibrary =
    Boolean(onAddToLibrary) && Boolean(selectedUrl) && Boolean(String(agentId || '').trim()) && !isAddingToLibrary
  const canApplyEdit =
    Boolean(selectedUrl) && Boolean(String(agentId || '').trim()) && !isApplyingEdit && !isGenerating

  const handleGenerate = async () => {
    const resolvedAgentId = String(agentId || '').trim()
    if (!resolvedAgentId) {
      setEditError('Image agent is missing. Select a live image agent before generating.')
      return
    }
    const nextPrompt = String(prompt || '').trim()
    if (!nextPrompt) return

    try {
      setEditError('')
      await onGenerate({
        prompt: nextPrompt,
        aspectRatio: String(selectedRatio || 'auto'),
        agentId: resolvedAgentId,
      })
    } catch (error: any) {
      setEditError(String(error?.message || 'Unable to generate image right now.'))
    }
  }

  const handleAddToLibrary = async () => {
    if (!onAddToLibrary || !selectedUrl) return
    const resolvedAgentId = String(agentId || '').trim()
    if (!resolvedAgentId) {
      setEditError('Image agent is missing. Cannot add without agent token.')
      return
    }

    try {
      setEditError('')
      await onAddToLibrary({
        url: selectedUrl,
        agentId: resolvedAgentId,
      })
    } catch (error: any) {
      setEditError(String(error?.message || 'Unable to add image to media library.'))
    }
  }

  const handleApplyEdit = async () => {
    if (!selectedUrl) return
    const resolvedAgentId = String(agentId || '').trim()
    if (!resolvedAgentId) {
      setEditError('Image agent is missing. Cannot apply edits without agent token.')
      return
    }

    const crop = {
      x: clamp(cropX, 0, 99),
      y: clamp(cropY, 0, 99),
      width: clamp(cropWidth, 1, 100),
      height: clamp(cropHeight, 1, 100),
    }
    const adjust = {
      brightness: clamp(brightness, 20, 220),
      contrast: clamp(contrast, 20, 220),
      saturation: clamp(saturation, 0, 240),
      grayscale: clamp(grayscale, 0, 100),
      blur: clamp(blur, 0, 12),
    }

    try {
      setIsApplyingEdit(true)
      setEditError('')

      let nextUrl = ''
      if (onApplyEdit) {
        const response = await onApplyEdit({
          url: selectedUrl,
          agentId: resolvedAgentId,
          crop,
          adjust,
        })
        nextUrl = typeof response === 'string' ? String(response || '').trim() : ''
      }

      if (!nextUrl) {
        const blob = await applyLocalEditToUrl(selectedUrl, crop, adjust)
        nextUrl = URL.createObjectURL(blob)
        objectUrlsRef.current.push(nextUrl)
      }

      if (!nextUrl) {
        throw new Error('No edited image output was returned.')
      }

      setWorkingUrls((previous) => {
        const nextUrls = [...previous, nextUrl]
        setSelectedIndex(nextUrls.length - 1)
        onOutputsChange?.(nextUrls, resolvedAgentId)
        return nextUrls
      })
    } catch (error: any) {
      setEditError(String(error?.message || 'Unable to apply image edits.'))
    } finally {
      setIsApplyingEdit(false)
    }
  }

  const handleResetAdjustments = () => {
    setCropX(0)
    setCropY(0)
    setCropWidth(100)
    setCropHeight(100)
    setBrightness(100)
    setContrast(100)
    setSaturation(100)
    setGrayscale(0)
    setBlur(0)
    setEditError('')
  }

  return (
    <div className='position-relative'>
      {isGenerating && (
        <div className='z-index-10 position-absolute top-0 bottom-0 right-0 left-0 d-flex align-items-center justify-content-center bg-white'>
          <div className='spinner-border text-primary' role='status' aria-label='Generating image'>
            <span className='visually-hidden'>Generating image</span>
          </div>
        </div>
      )}

      <div className='mt-4 mb-3'>
        <label className='form-label mb-2'>{title}</label>
        <textarea
          className='form-control mb-2'
          rows={3}
          placeholder={promptPlaceholder}
          value={prompt}
          onChange={(event) => onPromptChange(event.target.value)}
        />

        <div className='d-flex flex-wrap gap-2 align-items-center'>
          <select
            className='form-select form-select-sm w-auto'
            value={selectedRatio}
            onChange={(event) => onRatioChange?.(event.target.value)}
          >
            {ratioOptions.map((ratio) => (
              <option key={ratio} value={ratio}>
                {ratio === 'auto' ? 'Auto Ratio' : ratio}
              </option>
            ))}
          </select>

          <button
            type='button'
            className='btn btn-primary text-white'
            disabled={!canGenerate}
            onClick={() => {
              void handleGenerate()
            }}
          >
            {isGenerating ? generatingLabel : generateLabel}
          </button>

          {onAddToLibrary && (
            <button
              type='button'
              className='btn btn-outline-primary'
              disabled={!canAddToLibrary}
              onClick={() => {
                void handleAddToLibrary()
              }}
            >
              {isAddingToLibrary ? addingToLibraryLabel : addToLibraryLabel}
            </button>
          )}
        </div>
      </div>

      {workingUrls.length > 0 && (
        <>
          <div className='mb-3 border rounded p-2 bg-light'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <strong className='small text-uppercase text-muted'>Canvas</strong>
              <span className='small text-muted'>Output {selectedIndex + 1} of {workingUrls.length}</span>
            </div>
            <div
              className='border rounded bg-white overflow-hidden d-flex align-items-center justify-content-center'
              style={{ minHeight: 280 }}
            >
              {selectedUrl ? (
                <img
                  src={selectedUrl}
                  alt='Generated preview'
                  style={{ width: '100%', maxHeight: 460, objectFit: 'contain' }}
                />
              ) : (
                <span className='small text-muted'>No image selected</span>
              )}
            </div>
          </div>

          <div className='mb-3 border rounded p-2'>
            <p className='small fw-semibold mb-2'>Edit Tools</p>
            <div className='row g-2'>
              <div className='col-6 col-md-3'>
                <label className='form-label small mb-1'>Crop X (%)</label>
                <input type='number' className='form-control form-control-sm' value={cropX} min={0} max={99} onChange={(e) => setCropX(Number(e.target.value || 0))} />
              </div>
              <div className='col-6 col-md-3'>
                <label className='form-label small mb-1'>Crop Y (%)</label>
                <input type='number' className='form-control form-control-sm' value={cropY} min={0} max={99} onChange={(e) => setCropY(Number(e.target.value || 0))} />
              </div>
              <div className='col-6 col-md-3'>
                <label className='form-label small mb-1'>Crop W (%)</label>
                <input type='number' className='form-control form-control-sm' value={cropWidth} min={1} max={100} onChange={(e) => setCropWidth(Number(e.target.value || 100))} />
              </div>
              <div className='col-6 col-md-3'>
                <label className='form-label small mb-1'>Crop H (%)</label>
                <input type='number' className='form-control form-control-sm' value={cropHeight} min={1} max={100} onChange={(e) => setCropHeight(Number(e.target.value || 100))} />
              </div>

              <div className='col-6 col-md-4'>
                <label className='form-label small mb-1'>Brightness ({brightness}%)</label>
                <input type='range' className='form-range' min={20} max={220} value={brightness} onChange={(e) => setBrightness(Number(e.target.value))} />
              </div>
              <div className='col-6 col-md-4'>
                <label className='form-label small mb-1'>Contrast ({contrast}%)</label>
                <input type='range' className='form-range' min={20} max={220} value={contrast} onChange={(e) => setContrast(Number(e.target.value))} />
              </div>
              <div className='col-6 col-md-4'>
                <label className='form-label small mb-1'>Saturation ({saturation}%)</label>
                <input type='range' className='form-range' min={0} max={240} value={saturation} onChange={(e) => setSaturation(Number(e.target.value))} />
              </div>
              <div className='col-6 col-md-6'>
                <label className='form-label small mb-1'>Grayscale ({grayscale}%)</label>
                <input type='range' className='form-range' min={0} max={100} value={grayscale} onChange={(e) => setGrayscale(Number(e.target.value))} />
              </div>
              <div className='col-6 col-md-6'>
                <label className='form-label small mb-1'>Blur ({blur.toFixed(1)}px)</label>
                <input type='range' className='form-range' min={0} max={12} step={0.2} value={blur} onChange={(e) => setBlur(Number(e.target.value))} />
              </div>
            </div>

            <div className='d-flex flex-wrap gap-2 mt-2'>
              <button
                type='button'
                className='btn btn-sm btn-primary text-white'
                disabled={!canApplyEdit}
                onClick={() => {
                  void handleApplyEdit()
                }}
              >
                {isApplyingEdit ? 'Applying...' : 'Apply Edit'}
              </button>
              <button
                type='button'
                className='btn btn-sm btn-light border'
                onClick={handleResetAdjustments}
                disabled={isApplyingEdit}
              >
                Reset Tools
              </button>
            </div>
          </div>
        </>
      )}

      {editError && <div className='alert alert-warning py-2 small mb-2'>{editError}</div>}

      <div className='mt-3'>
        <p className='small text-muted mb-2'>{resultLabel}</p>
        {workingUrls.length === 0 ? (
          <p className='small text-muted mb-0'>No outputs yet.</p>
        ) : (
          <div className='d-flex flex-wrap gap-2'>
            {workingUrls.map((url, index) => (
              <button
                type='button'
                key={`${url}-${index}`}
                className={`p-0 border rounded overflow-hidden ${index === selectedIndex ? 'border-primary' : 'border-light'}`}
                onClick={() => setSelectedIndex(index)}
                style={{ width: 88, height: 88 }}
                title={`Output ${index + 1}`}
              >
                <img
                  src={url}
                  alt={`Generated output ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MediaGeneratePanel
